import { Grid, GridColumn, GridRow, Input } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  SchoolYearInput,
  useCreateSchoolYearsMutation,
  useUpdateSchoolYearsMutation,
} from '../../../types/planung-graphql-client-defs';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { SchoolYearType } from '../graphql/types';
import { DatePickerSchoolYearsField } from './DatePickerSchoolYearsField';
import { schema } from './validation/schema';

type SchoolYearFormProps = {
  schoolYear: SchoolYearType | null;
  others: SchoolYearType[] | null;
  handleClose: () => void;
};

export type SchoolYearListType = {
  name: string;
  shortName: string;
  fullTimeHours?: number | null;
  /** Im Formular echte Dates - die API liefert/erwartet Date-Scalars als 'yyyy-mm-dd'-Strings. */
  start: Date | undefined;
  end: Date | undefined;
};

export const SchoolYearForm = ({ schoolYear, others, handleClose }: SchoolYearFormProps) => {
  const { t } = useTranslation();

  const context = useMemorizedCacheTag('SCHOOL_YEAR');

  const [, create] = useCreateSchoolYearsMutation();
  const [, update] = useUpdateSchoolYearsMutation();
  const [validateOnChange, setValidateOnChange] = useState(!!schoolYear);

  const initialValue: SchoolYearListType = {
    name: schoolYear?.name ?? '',
    shortName: schoolYear?.shortName ?? '',
    fullTimeHours: schoolYear?.fullTimeHours,
    start: schoolYear?.start ? new Date(schoolYear.start) : undefined,
    end: schoolYear?.end ? new Date(schoolYear.end) : undefined,
  };

  const handleSubmit = async (values: SchoolYearListType, formHelpers: FormikHelpers<SchoolYearListType>) => {
    let mutationResult;

    // Das Formular erzwingt Beginn und Ende per Yup-Schema. Der DatePicker liefert das gewählte Datum als
    // Mitternacht in UTC; gesendet wird davon nur das Kalenderdatum (yyyy-mm-dd).
    const input: SchoolYearInput = {
      name: values.name,
      shortName: values.shortName,
      fullTimeHours: (values.fullTimeHours as unknown as string) === '' ? 0 : values.fullTimeHours,
      start: values.start?.toISOString().slice(0, 10) ?? '',
      end: values.end?.toISOString().slice(0, 10) ?? '',
    };

    if (schoolYear) {
      mutationResult = await update({ uuid: schoolYear.uuid, input }, context);
      !mutationResult && showSuccessUpdateToast();
    } else {
      mutationResult = await create({ input }, context);
      !mutationResult && showSuccessCreateToast();
    }
    if (!mutationResult || mutationResult.error) {
      showUserErrorToast({ error: '' });
    }
    formHelpers.resetForm();
    handleClose();
  };

  return (
    <Formik
      validationSchema={() => {
        return schema(others);
      }}
      initialValues={initialValue}
      onSubmit={handleSubmit}
      validateOnChange={validateOnChange}
      validateOnBlur={false}
    >
      {(formik) => {
        const values = formik.values;
        const errors = formik.errors;
        const touched = formik.touched;

        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <GridColumn width={10}>
                  <Input
                    label={t('schoolYear.name')}
                    name={'name'}
                    onChange={formik.handleChange}
                    value={values.name}
                    {...(errors.name &&
                      touched.name && {
                        error: errors.name,
                      })}
                  />
                </GridColumn>
                <GridColumn width={2}>
                  <Input
                    label={t('schoolYear.shortName')}
                    name={'shortName'}
                    onChange={formik.handleChange}
                    value={values.shortName}
                    {...(errors.shortName &&
                      touched.shortName && {
                        error: errors.shortName,
                      })}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop='none' spacingBottom='none'>
                <GridColumn width={6}>
                  <DatePickerSchoolYearsField
                    label={t('schoolYear.start')}
                    fieldName={'start'}
                    validateOnChange={validateOnChange}
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <DatePickerSchoolYearsField
                    label={t('schoolYear.end')}
                    fieldName={'end'}
                    validateOnChange={validateOnChange}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop='s'>
                <GridColumn width={4}>
                  <Input
                    name={'fullTimeHours'}
                    value={values.fullTimeHours}
                    onChange={async (e) => {
                      const hours = parseFloat(e.target.value);
                      await formik.setFieldValue('fullTimeHours', isNaN(hours) ? null : hours);
                    }}
                    label={t('contracts.fullTimeHours')}
                    type={'number'}
                    error={formik.errors.fullTimeHours}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                text: t('common.cancelChanges'),
                callback: () => {
                  formik.resetForm();
                  handleClose();
                },
              }}
              submitButton={{
                disabled: formik.isSubmitting || !formik.dirty || formik.isValidating,
                callback: () => {
                  setValidateOnChange(true);
                  formik.handleSubmit();
                },
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
